import React from 'react'
import Bugsnag, { Event, OnErrorCallback } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import APP_VERSION from 'app-version'

import { sleep } from 'utilities/promises'
import {
  templateSecretsDeep,
  templateSecretsInText,
} from 'utilities/secret-urls'

// Key names whose values are credentials, replaced with "[REDACTED]" at every
// depth of metadata and breadcrumb metadata when a report is sent. Keep in
// step with BUGSNAG_FILTERS in node/utilities/error/redact-secrets.ts. Setting
// this replaces Bugsnag's default of ['password'].
export const REDACTED_KEYS: RegExp[] = [
  /password/i,
  // Ends in "token" so counts such as promptTokens survive.
  /token$/i,
  /secret/i,
  /^authorization$/i,
  /cookie/i,
  /api[-_]?key/i,
  /^meetingCode$/i,
  /^(kickoffEventRef|nylasBookingRef|bookingRef)$/i,
]

// The page address rides on every report: in its context, its request URL,
// navigation and request breadcrumbs, and often the error message. On a /meet
// page that address is the join link, so each URL is templated before the
// report leaves (utilities/secret-urls has the list of routes).
export const templateSecretsInEvent = (event: Event) => {
  if (event.context) event.context = templateSecretsInText(event.context)
  if (event.request?.url) {
    event.request.url = templateSecretsInText(event.request.url)
  }
  for (const error of event.errors) {
    error.errorMessage = templateSecretsInText(error.errorMessage)
  }
  for (const breadcrumb of event.breadcrumbs) {
    breadcrumb.message = templateSecretsInText(breadcrumb.message)
    breadcrumb.metadata = templateSecretsDeep(
      breadcrumb.metadata
    ) as typeof breadcrumb.metadata
  }
  // Event has no public way to list its metadata sections.
  const metadata = (event as unknown as { _metadata?: unknown })._metadata
  if (metadata && typeof metadata === 'object') {
    for (const [section, values] of Object.entries(metadata)) {
      if (!values || typeof values !== 'object') continue
      const templated = templateSecretsDeep(values)
      // An Error or other class instance comes back as itself; re-adding it
      // would keep only its enumerable fields.
      if (templated === values) continue
      event.clearMetadata(section)
      event.addMetadata(section, templated as Record<string, unknown>)
    }
  }
}

const bugsnagClient = Bugsnag.start({
  apiKey:
    typeof window !== 'undefined'
      ? process.env.BUGSNAG_BROWSER_API_KEY
      : process.env.BUGSNAG_SSR_API_KEY,
  releaseStage: process.env.APP_ENV,
  enabledReleaseStages: ['staging', 'production', 'growth'],
  plugins: [new BugsnagPluginReact(React)],
  appVersion: APP_VERSION,
  redactedKeys: REDACTED_KEYS,
})

// Templating has to run after every other callback, not as the client's
// `onError`: Bugsnag runs a report's own callback last (`notify(error, cb)`,
// the React error boundary, the unhandled-rejection handler), and those set
// context and add metadata too. Every report goes through `_notify`, which
// concatenates the client's callbacks with the report's, so an array runs each
// in turn with Bugsnag's usual sync and async handling.
const notifyWithCallbacks = bugsnagClient._notify.bind(bugsnagClient)
bugsnagClient._notify = (event, onError, postReportCallback) =>
  notifyWithCallbacks(
    event,
    ([] as OnErrorCallback[]).concat(
      onError ?? [],
      templateSecretsInEvent
    ) as unknown as OnErrorCallback,
    postReportCallback
  )

export const bugsnagNotifyAsync = async error => {
  await Promise.race([
    sleep(2000), // timeout
    new Promise(resolve => {
      Bugsnag.notify(error, () => {
        console.log('Bugsnag notified!')
        resolve(true)
      })
    }),
  ])
}

export default bugsnagClient
